import './assets/main.css'
import '@/ui/tokens/index.css'
import './assets/animations.css'
import './assets/scrollbar.css'


import { ViteSSG } from 'vite-ssg'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import Tooltip from 'primevue/tooltip'
import FocusTrap from 'primevue/focustrap'

import App from './App.vue'
import { routes, scrollBehavior, registerRouterGuards, setActiveRouter } from '@/router'
import { MyPreset } from './themes/primevue-preset'
import { i18n } from '@/locales'
import { bindFairHead } from '@/seo/fairHead'
import { applyFairSeo } from '@/composables/useFairSeo'
import { getFairRouteSeo } from '@/seo/routeSeo'

import ClickOutside from '@/directives/ClickOutsideDirective'
import { vCopy } from '@/directives/vCopy'
import type { App as VueApp } from 'vue'

function registerDirectives(app: VueApp): void {
  app.directive('focustrap', FocusTrap)
  app.directive('tooltip', Tooltip)
  app.directive('click-outside', ClickOutside)
  app.directive('copy', vCopy)
}

async function registerClientOnly(): Promise<void> {
  const { register } = await import('swiper/element/bundle')
  register()

  if (!import.meta.env.DEV) {
    return
  }

  ;(window as any).openSurvey = async () => {
    const { useSizesQuizStore } = await import('@/features/sizes-quiz/store')
    useSizesQuizStore().open()
  }
  console.log('💡 Development helper: Use window.openSurvey() to open "Find my sizes"')

  ;(window as any).openLifestyleQuiz = async () => {
    const { useLifestyleQuizStore } = await import('@/features/lifestyle-quiz/store')
    useLifestyleQuizStore().open()
  }

  ;(window as any).openTasteSurvey = async (mode?: 'rate') => {
    const { useStyleQuizStore } = await import('@/features/style-quiz/store')
    const styleQuiz = useStyleQuizStore()
    if (mode === 'rate') styleQuiz.openRate()
    else styleQuiz.open()
  }
  console.log("💡 Development helper: Use window.openTasteSurvey() (or openTasteSurvey('rate')) to open the Style quiz")

  ;(window as any).openGiftSurvey = async () => {
    const { useGiftQuizStore } = await import('@/features/gift-quiz/store')
    useGiftQuizStore().open()
  }
  console.log('💡 Development helper: Use window.openGiftSurvey() to open the gift quiz')

  ;(window as any).openLookSurvey = async () => {
    const { useOutfitQuizStore } = await import('@/features/outfit-quiz/store')
    useOutfitQuizStore().open()
  }
  console.log('💡 Development helper: Use window.openLookSurvey() to open the outfit quiz')
}

export const createApp = ViteSSG(
  App,
  {
    routes,
    scrollBehavior,
    base: import.meta.env.BASE_URL,
  },
  async ({ app, router, head, isClient }) => {
    if (head) {
      bindFairHead(head as never)
    }

    const pinia = createPinia()
    app.use(pinia)
    app.use(i18n)
    app.use(PrimeVue, {
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.fake-dark-selector',
          cssLayer: {
            name: 'primevue',
            order: 'tailwind-base, primevue, tailwind-utilities',
          },
        },
      },
    })
    registerDirectives(app)

    setActiveRouter(router)
    registerRouterGuards(router)

    // Ensure Home SEO (incl. JSON-LD) is on the Unhead instance used by vite-ssg
    applyFairSeo(getFairRouteSeo('home', '/'))

    if (isClient) {
      await registerClientOnly()
      // lift the pre-hydration curtain (index.html) once the REAL route is
      // resolved — without this a hard refresh of any deep page flashes the
      // prerendered home markup for a second or two.
      // FE-003.3 — hand off to the branded overlay instead of leaving a gap:
      // the router guard has already started the loader by the time isReady
      // settles, so wait one painted frame for the overlay to be on screen
      // before uncovering #app.
      router.isReady().finally(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.documentElement.classList.remove('boot-pending')
          })
        })
      })
    }
  },
)
